/*
 * Independent pagination for Ajeg product grids (`sections/ajeg-product-grid.liquid`).
 *
 * WHY THIS EXISTS
 * Shopify's `{% paginate %}` tag only reads the shared `?page=` query parameter
 * when the array being paginated is `collection.products`. Unlike
 * `article_list` / `collection_list` / `product_list` settings — which get a
 * unique key appended to the parameter (`paginate.page_param`) — a paginated
 * `collection.products` array always uses bare `page`. So two product-grid
 * sections on the same template cannot be paged independently through their
 * server-rendered links: clicking either pager advances both grids.
 *
 * This script upgrades the server-rendered pager to fetch and swap a single
 * section via the Section Rendering API (`?page=N&section_id=<id>`), the same
 * pattern already used by `ensureAllCollectionItems()` in `assets/ajeg.js`.
 * Each grid then keeps its own page state while the other grid is untouched.
 *
 * PROGRESSIVE ENHANCEMENT
 * The markup keeps the original `?page=N#anchor` hrefs, so without JavaScript
 * the pager still works (with the legacy "both grids move" behaviour). This
 * script only intercepts when JS is available.
 *
 * Deep links: each grid stores its own page in its own query parameter
 * (`?aj_p_<key>=2`), so a link can be bookmarked or shared and the right grid
 * is restored on load. The shared `page` parameter is never written.
 */
(() => {
  // The section markup is rendered once per grid, so this script tag can appear
  // several times on a page. Initialise once.
  if (window.__ajGridPaginationReady) return;
  window.__ajGridPaginationReady = true;

  const SECTION_SELECTOR = '.aj-product-grid[data-aj-grid-id]';
  const BODY_SELECTOR = '[data-aj-grid-body]';
  const PAGE_PARAM_PREFIX = 'aj_p_';
  const SECTION_ID_PREFIX = 'aj-product-grid-';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Readable, collision-free URL key derived from the dynamic section id suffix
  // (e.g. `template--1234__must_have` -> `aj_p_must_have`).
  function pageParam(sectionId) {
    const marker = sectionId.lastIndexOf('__');
    const key = marker === -1 ? sectionId : sectionId.slice(marker + 2);
    return PAGE_PARAM_PREFIX + key.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  function pageFromHref(href) {
    if (!href) return null;
    try {
      const value = new URL(href, window.location.origin).searchParams.get('page');
      const page = Number.parseInt(value, 10);
      return Number.isFinite(page) && page > 0 ? page : null;
    } catch {
      return null;
    }
  }

  // The theme header is `position: sticky` (assets/ajeg.css), so a plain
  // `scrollIntoView` would tuck the section title under it. Offset the scroll
  // target by the header's live height.
  function keepBelowStickyHeader(section) {
    const header = document.querySelector('.aj-header');
    const offset = header ? Math.max(header.getBoundingClientRect().height, 0) : 0;
    section.style.scrollMarginTop = `${offset + 16}px`;
  }

  function currentPageOf(section) {
    const wrapper = section.querySelector('.pagination-wrapper[data-page]');
    const page = Number.parseInt(wrapper?.dataset.page || '', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  // Keep a per-grid parameter in the address bar without ever writing the
  // shared `page` parameter, so a reload does not move the other grid.
  function syncUrl(sectionId, page) {
    try {
      const url = new URL(window.location.href);
      const param = pageParam(sectionId);
      if (page > 1) url.searchParams.set(param, String(page));
      else url.searchParams.delete(param);
      url.hash = SECTION_ID_PREFIX + sectionId;
      history.replaceState(history.state, '', url.toString());
    } catch {
      /* URL is cosmetic; never let it break pagination. */
    }
  }

  function render(section, html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(SECTION_ID_PREFIX + section.dataset.ajGridId);
    if (!fresh) return false;

    const target = section.querySelector(BODY_SELECTOR);
    const source = fresh.querySelector(BODY_SELECTOR);
    if (target && source) target.innerHTML = source.innerHTML;
    else section.innerHTML = fresh.innerHTML;
    return true;
  }

  function loadPage(section, page, options = {}) {
    const { updateUrl = true, moveFocus = true, scroll = true } = options;
    const sectionId = section.dataset.ajGridId;
    if (!sectionId || section.hasAttribute('aria-busy')) return Promise.resolve();

    // Build from the current URL so unrelated parameters (filters, sort,
    // preview_theme_id, ...) survive; only the requested section is rendered.
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);

    section.setAttribute('aria-busy', 'true');

    return fetch(url.toString(), { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (!render(section, html)) throw new Error('section not found in response');
        if (updateUrl) syncUrl(sectionId, page);
        if (moveFocus) {
          // Move focus to the updated grid so assistive tech follows the swap.
          // The container is not keyboard-reachable, so suppress the focus ring.
          section.setAttribute('tabindex', '-1');
          section.style.outline = 'none';
          section.focus({ preventScroll: true });
        }
        if (scroll) {
          keepBelowStickyHeader(section);
          section.scrollIntoView({
            block: 'start',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        }
      })
      .catch((error) => {
        // Leave the current grid intact rather than navigating to a URL that
        // would move every grid on the page.
        console.warn('[ajeg-grid-pagination] could not load page', page, error);
      })
      .finally(() => {
        section.removeAttribute('aria-busy');
      });
  }

  // Upgrade every pager on the page (delegated, so swapped markup needs no
  // re-binding after a fetch).
  document.addEventListener('click', (event) => {
    // Respect "open in new tab/window" gestures.
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('.aj-product-grid__pagination a.pagination__item');
    if (!link || link.target === '_blank') return;
    const section = link.closest(SECTION_SELECTOR);
    if (!section) return;
    const page = pageFromHref(link.getAttribute('href'));
    if (!page || page === currentPageOf(section)) return;
    event.preventDefault();
    loadPage(section, page);
  });

  // Deep-link restore: apply each grid's own page parameter on load.
  function hydrate() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const sectionId = section.dataset.ajGridId;
      const value = new URL(window.location.href).searchParams.get(pageParam(sectionId));
      const page = Number.parseInt(value || '', 10);
      if (Number.isFinite(page) && page > 1) {
        loadPage(section, page, { updateUrl: false, moveFocus: false, scroll: false });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hydrate);
  else hydrate();
})();
